import React, { useEffect, useMemo, useState } from "react";
import {
 Row,
 Col,
 Card,
 Badge,
 Spinner,
 Table,
 Accordion,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";

/* =============================
 HELPERS
============================= */
const sumAmount = (arr, key = "totalAmount") =>
 arr.reduce((sum, i) => sum + Number(i[key] || 0), 0);

/* =============================
 STATUS COLOR HELPER
============================= */
const getStatusColor = (status = "") => {
 const s = status.toLowerCase();

 if (["approved", "accepted", "completed", "success", "paid"].includes(s))
 return "success";

 if (["rejected", "cancelled", "failed"].includes(s))
 return "danger";

 if (["pending", "waiting", "processing"].includes(s))
 return "warning";

 if (["draft", "created"].includes(s))
 return "secondary";

 if (["in progress", "dispatch", "shipped"].includes(s))
 return "info";

 return "dark";
};

const PurchaseOrderOverview = () => {
 const { search } = useLocation();
 const workOrderId = new URLSearchParams(search).get("workOrder");

 const [loading, setLoading] = useState(true);

 const [indents, setIndents] = useState([]);
 const [purchaseOrders, setPurchaseOrders] = useState([]);
 const [deliveries, setDeliveries] = useState([]);
 const [purchaseInvoices, setPurchaseInvoices] = useState([]);
 const [handovers, setHandovers] = useState([]);
 const [documentsByType, setDocumentsByType] = useState({});
 const [metadata, setMetadata] = useState({});

 /* =============================
 FETCH DATA
 ============================= */
 useEffect(() => {
 if (!workOrderId) return;

 const load = async () => {
 try {
 setLoading(true);

 const res = await api.get(
 `/purchase-orders/overview?workOrder=${workOrderId}`
 );

 if (res.data.success) {
 const d = res.data.data || {};
 setIndents(d.indents || []);
 setPurchaseOrders(d.purchaseOrders || []);
 setDeliveries(d.deliveries || []);
 setPurchaseInvoices(d.purchaseInvoices || []);
 setHandovers(d.handovers || []);
 setDocumentsByType(d.documentsByType || {});
 setMetadata(d.metadata || {});
 }
 } catch (err) {
 console.error("Purchase overview error:", err);
 } finally {
 setLoading(false);
 }
 };

 load();
 }, [workOrderId]);

 /* =============================
 SUMMARY
 ============================= */
 const summary = useMemo(
 () => ({
 indentCount: indents.length,
 poCount: purchaseOrders.length,
 poValue: sumAmount(purchaseOrders),
 deliveryCount: deliveries.length,
 invoiceCount: purchaseInvoices.length,
 handoverCount: handovers.length,
 }),
 [indents, purchaseOrders, deliveries, purchaseInvoices, handovers]
 );

 if (!workOrderId) {
 return (
 <div className="text-center py-5 text-muted">
 Please open this page from a Work Order
 </div>
 );
 }

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner />
 </div>
 );
 }

 return (
 <>
 <PageTitle activeMenu="Purchase Overview" motherMenu="Commercial" />

 {/* ================= HEADER CARD (CONTEXT) ================= */}
 {metadata.workOrder && (
 <Card className="mb-4 shadow-sm">
 <Card.Body>
 <h5 className="fw-bold mb-1">{metadata.workOrder.woNo}</h5>
 <small className="text-muted">
 {metadata.workOrder.projectName || "Purchase Overview"}
 </small>
 </Card.Body>
 </Card>
 )}

 {/* ================= KPI CARDS ================= */}
 <Row className="g-3 mb-4">
 {[
 { label: "Indents", value: summary.indentCount },
 { label: "POs", value: summary.poCount },
 { label: "PO Value", value: `₹ ${summary.poValue.toLocaleString()}` },
 { label: "Deliveries", value: summary.deliveryCount },
 { label: "Invoices", value: summary.invoiceCount },
 { label: "Handovers", value: summary.handoverCount },
 ].map((c, i) => (
 <Col xs={6} md={4} lg={2} key={i}>
 <Card className="shadow-sm text-center h-100">
 <Card.Body>
 <small className="text-muted">{c.label}</small>
 <h5 className="fw-bold mt-1">{c.value}</h5>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* ================= ACCORDION ================= */}
 <Accordion alwaysOpen defaultActiveKey="0">

 {/* INDENTS */}
 <Accordion.Item eventKey="0">
 <Accordion.Header>Indents ({indents.length})</Accordion.Header>
 <Accordion.Body>
 {indents.length === 0 ? (
 <p className="text-muted">No indents found</p>
 ) : (
 <Table responsive size="sm" bordered hover>
 <thead>
 <tr>
 <th className="text-center">#</th>
 <th className="text-center">Indent No</th>
 <th className="text-center">Quantity</th>
 <th className="text-center">Status</th>
 <th className="text-center">Date</th>
 </tr>
 </thead>
 <tbody>
 {indents.map((i, idx) => (
 <tr key={i._id}>
 <td className="text-center">{idx + 1}</td>
 <td>{i.indentNumber}</td>
 <td className="text-center">{i.quantity || "-"}</td>
 <td className="text-center">
 <Badge bg={getStatusColor(i.status)}>
 {i.status}
 </Badge>
 </td>
 <td className="text-center">
 {new Date(i.createdAt).toLocaleDateString()}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Accordion.Body>
 </Accordion.Item>

 {/* PURCHASE ORDERS */}
 <Accordion.Item eventKey="1">
 <Accordion.Header>
 Purchase Orders ({purchaseOrders.length})
 </Accordion.Header>
 <Accordion.Body>
 <Table responsive size="sm" bordered hover>
 <thead>
 <tr>
 <th className="text-center">#</th>
 <th className="text-center">PO No</th>
 <th className="text-center">Quantity</th>
 <th className="text-center">Status</th>
 <th className="text-center">Amount</th>
 </tr>
 </thead>
 <tbody>
 {purchaseOrders.map((po, idx) => (
 <tr key={po._id}>
 <td className="text-center">{idx + 1}</td>
 <td>{po.poNumber}</td>
 <td className="text-center">{po.quantity || "-"}</td>
 <td className="text-center">
 <Badge bg={getStatusColor(po.status)}>
 {po.status}
 </Badge>
 </td>
 <td className="text-center">
 ₹ {po.totalAmount?.toLocaleString()}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Accordion.Body>
 </Accordion.Item>

 {/* DELIVERIES */}
 <Accordion.Item eventKey="2">
 <Accordion.Header>
 Deliveries / DC ({deliveries.length})
 </Accordion.Header>
 <Accordion.Body>
 <Table responsive size="sm" bordered hover>
 <thead>
 <tr>
 <th className="text-center">#</th>
 <th className="text-center">DC No</th>
 <th className="text-center">Quantity</th>
 <th className="text-center">Status</th>
 </tr>
 </thead>
 <tbody>
 {deliveries.map((d, idx) => (
 <tr key={d._id}>
 <td className="text-center">{idx + 1}</td>
 <td>{d.deliveryNumber || d.dcNumber}</td>
 <td className="text-center">{d.quantity || "-"}</td>
 <td className="text-center">
 <Badge bg={getStatusColor(d.status)}>
 {d.status}
 </Badge>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Accordion.Body>
 </Accordion.Item>

 {/* HANDOVERS */}
 <Accordion.Item eventKey="3">
 <Accordion.Header>Handovers ({handovers.length})</Accordion.Header>
 <Accordion.Body>
 {handovers.length === 0 ? (
 <p className="text-muted">No handovers found</p>
 ) : (
 <Table responsive size="sm" bordered hover>
 <thead>
 <tr>
 <th className="text-center">#</th>
 <th className="text-center">Handover No</th>
 <th className="text-center">Quantity</th>
 <th className="text-center">Status</th>
 <th className="text-center">Date</th>
 </tr>
 </thead>
 <tbody>
 {handovers.map((h, idx) => (
 <tr key={h._id}>
 <td className="text-center">{idx + 1}</td>
 <td>{h.hoNumber || "—"}</td>
 <td className="text-center">{h.quantity || "-"}</td>
 <td className="text-center">
 <Badge bg={getStatusColor(h.status)}>
 {h.status}
 </Badge>
 </td>
 <td className="text-center">
 {new Date(h.createdAt).toLocaleDateString()}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Accordion.Body>
 </Accordion.Item>

 {/* DOCUMENTS BY TYPE */}
 {Object.entries(documentsByType).map(([type, docs], idx) => (
 <Accordion.Item eventKey={`doc-${idx}`} key={type}>
 <Accordion.Header>
 {type} ({docs.length})
 </Accordion.Header>
 <Accordion.Body>
 {docs.length === 0 ? (
 <p className="text-muted">No documents</p>
 ) : (
 <Table responsive size="sm" bordered hover>
 <thead>
 <tr>
 <th className="text-center">#</th>
 <th className="text-center">File</th>
 <th className="text-center">Status</th>
 </tr>
 </thead>
 <tbody>
 {docs.map((d, i) => (
 <tr key={d._id}>
 <td className="text-center">{i + 1}</td>
 <td>{d.fileName}</td>
 <td className="text-center">
 <Badge bg={getStatusColor(d.status)}>
 {d.status}
 </Badge>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Accordion.Body>
 </Accordion.Item>
 ))}
 </Accordion>
 </>
 );
};

export default PurchaseOrderOverview;